import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { LayoutBox, RenderNote } from "@scenix/core";
import { componentPicture } from "../lib/componentEngine";
import {
  boxStyle,
  contentStyle,
  flatten,
  noteValue,
  stageStyle,
  type PlacedLeaf,
} from "../lib/layout";
import { ComponentBox } from "./ComponentBox";

interface LayoutStageProps {
  layout: LayoutBox;
  /** The segment's own instructions, which a box may overrule (ADR 000068). */
  segmentNotes?: readonly RenderNote[];
  /** A signed media URL per asset the layout draws on. */
  mediaOf: ReadonlyMap<string, string>;
  /** Where the segment is, in its own time. Null when nothing is playing. */
  intoSegmentSec: number | null;
  playing: boolean;
}

/**
 * A layout drawn in the browser (ADR 000066).
 *
 * Not a preview of the render. It is the same description rendered by a
 * different engine, which is the point of the description being a description
 * rather than a pile of ffmpeg arguments.
 *
 * It is also what makes a layout editable at all: somebody moving a box needs
 * to see the result now, and waiting on an encode to find out where the box
 * went is not editing.
 */
export function LayoutStage({
  layout,
  segmentNotes = [],
  mediaOf,
  intoSegmentSec,
  playing,
}: LayoutStageProps) {
  const leaves = flatten(layout);

  return (
    <div className="layout-stage" style={stageStyle(segmentNotes)}>
      {leaves.map((leaf, i) => (
        <LeafBox
          key={i}
          leaf={leaf}
          segmentNotes={segmentNotes}
          mediaOf={mediaOf}
          intoSegmentSec={intoSegmentSec}
          playing={playing}
        />
      ))}
    </div>
  );
}

function LeafBox({
  leaf,
  segmentNotes,
  mediaOf,
  intoSegmentSec,
  playing,
}: {
  leaf: PlacedLeaf;
  segmentNotes: readonly RenderNote[];
  mediaOf: ReadonlyMap<string, string>;
  intoSegmentSec: number | null;
  playing: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const footage = leaf.content.footage;
  const src = footage ? mediaOf.get(footage.assetId) : undefined;

  // The source's own shape, which is what a fit is measured against and which
  // CSS has no way to ask for (issue #217). Read off the element rather than
  // from the manifest, because a still and a component have no manifest and the
  // question is the same for all three.
  const [sourceAspect, setSourceAspect] = useState<number>();
  // How much material the source actually holds, which is the other thing the
  // same metadata answers and the one a hold turns on.
  const [sourceSeconds, setSourceSeconds] = useState<number>();
  const measure = useCallback((element: HTMLVideoElement | HTMLImageElement | null) => {
    if (!element) return;
    const [w, h] =
      element instanceof HTMLVideoElement
        ? [element.videoWidth, element.videoHeight]
        : [element.naturalWidth, element.naturalHeight];
    // Asked on mount as well as on the event, because an element that already
    // had its metadata when this mounted has no event left to fire, and zero
    // until it does.
    if (w > 0 && h > 0) setSourceAspect(w / h);
    // A stream still being written reports Infinity and an unreadable one NaN.
    // Neither is a length, and treating either as one would blank a box that
    // has plenty left.
    if (element instanceof HTMLVideoElement && Number.isFinite(element.duration)) {
      setSourceSeconds(element.duration);
    }
  }, []);
  // One callback for the two things a video's ref does, held still so that
  // React does not detach and reattach it on every render.
  const holdVideo = useCallback(
    (element: HTMLVideoElement | null) => {
      video.current = element;
      measure(element);
    },
    [measure],
  );

  // Every box is seeked to the same moment of the segment, offset into its own
  // source. That is what makes them one picture rather than several clips that
  // happen to be adjacent.
  const at = footage && intoSegmentSec !== null ? footage.range.startSec + intoSegmentSec : null;

  useEffect(() => {
    const element = video.current;
    if (!element || at === null) return;

    const seek = () => {
      if (Math.abs(element.currentTime - at) > 0.35) element.currentTime = at;
      if (playing) {
        const started = element.play() as Promise<void> | undefined;
        started?.catch(() => {
          // The browser's to refuse. The frame is in the right place either way.
        });
      } else {
        element.pause();
      }
    };

    if (element.readyState >= 1) seek();
    else element.addEventListener("loadedmetadata", seek, { once: true });
    return () => element.removeEventListener("loadedmetadata", seek);
    // Deliberately not reacting to `at` changing every frame: the element is
    // the thing moving while it plays, and assigning currentTime would fight it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, playing]);

  // Where in the segment this box's material stops, measured against the file's
  // own end rather than against `range.endSec`.
  //
  // That is the end the renderer measures against. It opens each source with
  // `-ss startSec -t <the segment's length>` and lets it run, so a range that
  // reaches past the material is not what stops the box: the bytes are
  // (apps/api/internal/render/compose.go). The two engines have to run out at
  // the same instant or the preview describes a frame nobody will make
  // (ADR 000066).
  //
  // Unknown until metadata arrives, and unknown draws the source: a box that
  // blanked itself while waiting to be told its own length would flash the
  // canvas at the start of every segment.
  const runsOutAtSec =
    footage && sourceSeconds !== undefined ? sourceSeconds - footage.range.startSec : undefined;
  // Nothing left to draw, and no note asking for the last frame to stand in for
  // it. Black is the renderer's default and had no way to happen here: a
  // `<video>` past its end shows its last frame, so a box held it whichever
  // value the note carried (issue #263, ADR 000068).
  const blank =
    runsOutAtSec !== undefined &&
    intoSegmentSec !== null &&
    intoSegmentSec >= runsOutAtSec &&
    noteValue("whenShort", leaf.notes, segmentNotes) !== "hold";

  const boxed = (content: ReactNode) => (
    <div
      className="layout-box"
      // A box with nothing left to draw is not painted at all, its own
      // background included. ffmpeg's overlay passes the canvas through where a
      // box has ended, and the background note is the colour of that box's pad
      // rather than of the frame under it, so it ends with the box.
      style={{ ...boxStyle(leaf, segmentNotes), ...(blank ? { background: "transparent" } : {}) }}
      title={leaf.label}
    >
      {content}
    </div>
  );

  if (leaf.content.component) {
    return boxed(
      <ComponentBox
        component={leaf.content.component}
        intoSegmentSec={intoSegmentSec}
        // Known without measuring: a component is drawn at a size this side
        // chooses rather than one it is handed.
        style={contentStyle(
          leaf,
          segmentNotes,
          componentPicture.widthPx / componentPicture.heightPx,
        )}
        label={leaf.label}
      />,
    );
  }
  if (leaf.content.still) {
    return boxed(
      <img
        src={leaf.content.still}
        alt={leaf.label}
        ref={measure}
        onLoad={(event) => measure(event.currentTarget)}
        style={contentStyle(leaf, segmentNotes, sourceAspect)}
      />,
    );
  }
  if (!src) {
    return boxed(<span className="layout-box-empty muted">{leaf.label || "No footage"}</span>);
  }
  return boxed(
    <video
      ref={holdVideo}
      onLoadedMetadata={(event) => measure(event.currentTarget)}
      src={src}
      // Muting is the layout's, and the browser has to be told separately from
      // the render: a box the layout silences must not be heard here either.
      muted={leaf.muted}
      playsInline
      preload="metadata"
      // Hidden rather than unmounted once the source has run out: an element
      // taken out and put back would cost a reload and a reseek every time
      // somebody scrubbed across the end.
      style={{
        ...contentStyle(leaf, segmentNotes, sourceAspect),
        ...(blank ? { visibility: "hidden" } : {}),
      }}
      aria-label={leaf.label || "A box of the layout"}
    />,
  );
}
